import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Bell } from 'lucide-react';
import { api, relativeTime } from '@watchparty/shared';
import { tmdbImage, parsePayload } from '@/lib/utils';
import { useNotifications } from '@/lib/NotificationContext';
import Avatar from '@/components/ui/Avatar';
// ---------------------------------------------------------------------------
// Notification type constants (matching server NotificationType enum)
// ---------------------------------------------------------------------------
const TYPE_DISCUSSION_MESSAGE = 1;
const TYPE_WATCHING = 2;
const TYPE_RATED = 3;
const TYPE_WORK_ADDED = 4;
const TYPE_WORK_SUGGESTED = 5;
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatNotificationText(item) {
    const name = _jsx("strong", { children: item.actorDisplayName });
    const work = item.workTitle ? _jsx("strong", { children: item.workTitle }) : null;
    const party = item.partyName ? _jsx("strong", { children: item.partyName }) : null;
    const payload = parsePayload(item.payload);
    switch (item.notificationType) {
        case TYPE_DISCUSSION_MESSAGE:
            return _jsxs(_Fragment, { children: [name, " sent a message", work ? _jsxs(_Fragment, { children: [" about ", work] }) : null, party ? _jsxs(_Fragment, { children: [" in ", party] }) : null] });
        case TYPE_WATCHING:
            return _jsxs(_Fragment, { children: [name, " started watching ", work] });
        case TYPE_RATED: {
            const rating = payload.rating;
            if (rating != null) {
                return _jsxs(_Fragment, { children: [name, " rated ", work, " ", rating, " star", rating !== 1 ? 's' : ''] });
            }
            return _jsxs(_Fragment, { children: [name, " rated ", work] });
        }
        case TYPE_WORK_ADDED:
            return _jsxs(_Fragment, { children: [name, " added ", work, party ? _jsxs(_Fragment, { children: [" to ", party] }) : null] });
        case TYPE_WORK_SUGGESTED:
            return _jsxs(_Fragment, { children: [name, " suggested ", work, party ? _jsxs(_Fragment, { children: [" for ", party] }) : null] });
        default:
            return _jsxs(_Fragment, { children: [name, " did something"] });
    }
}
function getNotificationLink(item) {
    switch (item.notificationType) {
        case TYPE_DISCUSSION_MESSAGE:
        case TYPE_WORK_ADDED:
        case TYPE_RATED:
        case TYPE_WATCHING:
            if (item.workId && item.partyId)
                return { path: `/works/${item.workId}`, search: { partyId: item.partyId } };
            if (item.partyId)
                return { path: `/parties/${item.partyId}` };
            return null;
        case TYPE_WORK_SUGGESTED:
            if (item.partyId)
                return { path: `/parties/${item.partyId}` };
            return null;
        default:
            return null;
    }
}
// ---------------------------------------------------------------------------
// NotificationRow
// ---------------------------------------------------------------------------
function NotificationRow({ item, onClick, }) {
    const link = getNotificationLink(item);
    const poster = tmdbImage(item.posterUrl, 'w92');
    return (_jsxs("div", { role: link ? 'button' : undefined, tabIndex: link ? 0 : undefined, onClick: () => link && onClick(link), onKeyDown: (e) => {
            if (link && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                onClick(link);
            }
        }, style: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            padding: '12px 16px',
            borderBottom: '1px solid var(--color-border)',
            cursor: link ? 'pointer' : 'default',
            background: item.isRead ? 'transparent' : 'var(--color-surface-sunken)',
            transition: 'background 0.15s',
        }, children: [_jsx(Avatar, { displayName: item.actorDisplayName, avatarUrl: item.actorAvatarUrl, avatarColor: item.actorAvatarColor, size: 32 }), _jsxs("div", { style: { flex: 1, minWidth: 0 }, children: [_jsx("p", { style: {
                            margin: 0,
                            fontSize: '0.875rem',
                            color: 'var(--color-text)',
                            fontFamily: 'var(--font-family-body)',
                            lineHeight: 1.4,
                        }, children: formatNotificationText(item) }), _jsx("p", { style: {
                            margin: '2px 0 0',
                            fontSize: '0.75rem',
                            color: 'var(--color-text-faint)',
                            fontFamily: 'var(--font-family-body)',
                        }, children: relativeTime(item.createdAt) })] }), poster && (_jsx("div", { style: {
                    width: '28px',
                    height: '42px',
                    flexShrink: 0,
                    borderRadius: 'var(--radius-sm)',
                    overflow: 'hidden',
                }, children: _jsx("img", { src: poster, alt: "", style: { width: '100%', height: '100%', objectFit: 'cover' } }) }))] }));
}
// ---------------------------------------------------------------------------
// NotificationsPage
// ---------------------------------------------------------------------------
export function NotificationsPage() {
    const { clearUnread, liveNotifications } = useNotifications();
    const navigate = useNavigate();
    const { data, isLoading } = useQuery({
        queryKey: ['notifications'],
        queryFn: () => api.get('/notifications'),
    });
    const items = useMemo(() => {
        const fetched = data?.items ?? [];
        if (liveNotifications.length === 0)
            return fetched;
        const fetchedIds = new Set(fetched.map((n) => n.notificationId));
        const unique = liveNotifications.filter((n) => !fetchedIds.has(n.notificationId));
        return [...unique, ...fetched];
    }, [data, liveNotifications]);
    useEffect(() => {
        api.post('/notifications/read', {}).catch(() => { });
        clearUnread();
    }, [clearUnread]);
    const handleClick = (link) => {
        navigate({ to: link.path, search: link.search ?? {} });
    };
    return (_jsxs("div", { style: { maxWidth: '800px', margin: '0 auto', padding: '24px 16px' }, children: [_jsx("h1", { style: {
                    fontFamily: 'var(--font-family-heading)',
                    fontSize: 'clamp(1.25rem, 3vw, 1.75rem)',
                    fontWeight: 700,
                    color: 'var(--color-text)',
                    marginBottom: '24px',
                }, children: "Notifications" }), isLoading ? (_jsxs("div", { style: { display: 'flex', justifyContent: 'center', padding: '64px 0' }, children: [_jsx("style", { children: `@keyframes spin { to { transform: rotate(360deg) } }` }), _jsx("div", { style: {
                            width: '24px',
                            height: '24px',
                            border: '2px solid var(--color-border)',
                            borderTopColor: 'var(--color-text)',
                            borderRadius: '50%',
                            animation: 'spin 0.6s linear infinite',
                        } })] })) : items.length === 0 ? (_jsxs("div", { style: {
                    textAlign: 'center',
                    padding: '80px 24px',
                    color: 'var(--color-text-faint)',
                }, children: [_jsx(Bell, { size: 40, style: { opacity: 0.3, marginBottom: '16px' } }), _jsx("p", { style: {
                            fontFamily: 'var(--font-family-body)',
                            fontSize: '0.9375rem',
                            fontWeight: 500,
                            color: 'var(--color-text-muted)',
                            marginBottom: '6px',
                        }, children: "No notifications yet" }), _jsx("p", { style: {
                            fontFamily: 'var(--font-family-body)',
                            fontSize: '0.8rem',
                            color: 'var(--color-text-faint)',
                        }, children: "When your party members rate or start watching something, you\u2019ll see it here." })] })) : (_jsx("div", { style: {
                    borderTop: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-md)',
                    overflow: 'hidden',
                }, children: items.map((item) => (_jsx(NotificationRow, { item: item, onClick: handleClick }, item.notificationId))) }))] }));
}
